import { WIZARD_QUESTION_IDS } from '../../constants.js';
import type {
  ConditionalQuestionDef,
  ConnectedAnswers,
  ConnectedCrowd,
  CrowdType,
  LocationGrounding,
  LocationType,
  RhythmAnswer,
  RhythmType,
} from '../../domain/types.js';
import { isValidGroundingRegion } from '../../domain/locationTimezone.js';
import { CONDITIONAL_ANSWER_SCHEMA_VERSION, isNonBlankValue } from '../../domain/types.js';

const V = CONDITIONAL_ANSWER_SCHEMA_VERSION;

export interface FieldDef {
  questionId: string;
  label: string;
  helper: string;
  required: boolean;
  skippable?: boolean;
}

export const GROUNDING_FIELDS: FieldDef[] = [
  {
    questionId: WIZARD_QUESTION_IDS.groundingName,
    label: 'Name / label / title',
    helper:
      'Is it a business, where you work, your school, your home, a location you\'ve always wanted to create a world for, put its name here, Microshaft, BBC TV Station, 12th ave school, Cornell',
    required: true,
  },
  {
    questionId: WIZARD_QUESTION_IDS.groundingRegion,
    label: 'City, region, and country',
    helper: 'Enter a city, a region (state or province), and a country, separated by commas — e.g. Vancouver, BC, Canada',
    required: true,
  },
  {
    questionId: WIZARD_QUESTION_IDS.groundingLanguage,
    label: 'Language',
    helper: 'English is assumed but feel free to use any language',
    required: false,
  },
];

export const CONNECTED_QUESTIONS: ConditionalQuestionDef[] = [
  {
    questionId: WIZARD_QUESTION_IDS.connectedLocationType,
    schemaVersion: V,
    controlType: 'long_text',
    prompt: 'Type of location',
    whyItMatters:
      'What do you do for a living, what is your company about, what do they sell, what kind of school, university, college, high school, middle school, elementary or primary school, this will be your description of what your location does and will affect the conversations',
    dependencies: [],
    required: true,
  },
  {
    questionId: WIZARD_QUESTION_IDS.connectedDescription,
    schemaVersion: V,
    controlType: 'long_text',
    prompt: 'Description',
    whyItMatters:
      'this is anything about the location that means something, I live in a 30 story condo highrise in the middle of downtown vancouver, my wife is a server at a highend sushi restaurant and we have no pets, have 3 tvs, and 2 laptops, 2 desktops, and many game machines. Just give it some detail and life, and guess what, you\'ll see it reflected in what you talk about.',
    dependencies: [WIZARD_QUESTION_IDS.connectedLocationType],
    required: true,
  },
  {
    questionId: WIZARD_QUESTION_IDS.connectedCrowd,
    schemaVersion: V,
    controlType: 'single_select',
    prompt: 'Who typically comes here',
    whyItMatters: 'Visitor patterns define cast opportunities and access rules.',
    dependencies: [WIZARD_QUESTION_IDS.connectedDescription],
    required: false,
  },
  {
    questionId: WIZARD_QUESTION_IDS.connectedRhythm,
    schemaVersion: V,
    controlType: 'single_select',
    prompt: 'Daily rhythm',
    whyItMatters: 'When things happen shapes episode timing and crowd density.',
    dependencies: [WIZARD_QUESTION_IDS.connectedCrowd],
    required: false,
    skippable: true,
  },
];

export const CROWD_OPTIONS: { value: CrowdType; label: string }[] = [
  { value: 'public_venue', label: 'Public venue' },
  { value: 'invited_private_allowed', label: 'Invited / Private / Allowed' },
  {
    value: 'members_owners_workers_students_faculty',
    label: 'Members / Owners / Workers / Students / Faculty',
  },
  { value: 'other', label: 'Other' },
];

export const RHYTHM_OPTIONS: { value: RhythmType; label: string }[] = [
  { value: 'steady_busy_all_day', label: 'Steady / Busy all day' },
  { value: 'busy_then_slow', label: 'Busy then Slow' },
  { value: 'seasonal_or_event_driven', label: 'Seasonal or event-driven' },
  { value: 'family_or_friend_driven', label: 'Family or Friend driven' },
  { value: 'other', label: 'Other' },
];

/** @deprecated v1 per-type questions — v2 uses unified CONNECTED_QUESTIONS. */
export function questionsForLocationType(_type: LocationType | null): ConditionalQuestionDef[] {
  return CONNECTED_QUESTIONS;
}

export function questionOptions(questionId: string): { value: string; label: string }[] {
  if (questionId === WIZARD_QUESTION_IDS.connectedCrowd) {
    return CROWD_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  }
  if (questionId === WIZARD_QUESTION_IDS.connectedRhythm) {
    return RHYTHM_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  }
  return [];
}

export const DEFAULT_GROUNDING_LANGUAGE = 'English';

export function emptyLocationGrounding(): LocationGrounding {
  return { name: '', region: '', language: '' };
}

/** UI value when language is unset on a fresh draft; empty after explicit clear. */
export function displayGroundingLanguage(
  location: LocationGrounding | null,
  languageExplicitlyCleared: boolean,
): string {
  if (languageExplicitlyCleared) return location?.language ?? '';
  const stored = location?.language ?? '';
  if (stored.trim()) return stored;
  return DEFAULT_GROUNDING_LANGUAGE;
}

export function emptyConnectedAnswers(): ConnectedAnswers {
  return {
    locationType: '',
    description: '',
    crowd: { type: 'public_venue' },
    rhythm: { skipped: true },
  };
}

export function trimGrounding(location: LocationGrounding): LocationGrounding {
  return {
    name: location.name.trim(),
    region: location.region.trim(),
    language: location.language.trim(),
  };
}

export interface ValidationIssue {
  fieldId: string;
  message: string;
}

export function groundingValidationIssues(location: LocationGrounding | null): ValidationIssue[] {
  const loc = location ?? emptyLocationGrounding();
  const issues: ValidationIssue[] = [];
  if (!isNonBlankValue(loc.name)) {
    issues.push({ fieldId: WIZARD_QUESTION_IDS.groundingName, message: 'Name / label / title is required.' });
  }
  if (!isNonBlankValue(loc.region)) {
    issues.push({ fieldId: WIZARD_QUESTION_IDS.groundingRegion, message: 'City, region, and country are required.' });
  } else if (!isValidGroundingRegion(loc.region)) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.groundingRegion,
      message: 'Enter a city, a region, and a country separated by commas (e.g. Vancouver, BC, Canada).',
    });
  }
  return issues;
}

export function validateGrounding(location: LocationGrounding | null): string | null {
  return groundingValidationIssues(location)[0]?.message ?? null;
}

export function firstIncompleteGroundingField(location: LocationGrounding | null): string | null {
  return groundingValidationIssues(location)[0]?.fieldId ?? null;
}

export function connectedValidationIssues(connected: ConnectedAnswers | null): ValidationIssue[] {
  if (!connected) {
    return [
      { fieldId: WIZARD_QUESTION_IDS.connectedLocationType, message: 'Type of location is required.' },
      { fieldId: WIZARD_QUESTION_IDS.connectedDescription, message: 'Description is required.' },
    ];
  }
  const issues: ValidationIssue[] = [];
  if (!isNonBlankValue(connected.locationType)) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.connectedLocationType,
      message: 'Type of location is required.',
    });
  }
  if (!isNonBlankValue(connected.description)) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.connectedDescription,
      message: 'Description is required.',
    });
  }
  if (
    connected.crowd?.type === 'other' &&
    !isNonBlankValue(connected.crowd.otherDescription)
  ) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.connectedCrowd,
      message: 'Describe who typically comes here.',
    });
  }
  if (
    !connected.rhythm.skipped &&
    connected.rhythm.type === 'other' &&
    !isNonBlankValue(connected.rhythm.otherDescription)
  ) {
    issues.push({
      fieldId: WIZARD_QUESTION_IDS.connectedRhythm,
      message: 'Describe the daily rhythm.',
    });
  }
  return issues;
}

export function validateConnected(connected: ConnectedAnswers | null): string | null {
  return connectedValidationIssues(connected)[0]?.message ?? null;
}

export function firstIncompleteConnectedField(connected: ConnectedAnswers | null): string | null {
  return connectedValidationIssues(connected)[0]?.fieldId ?? null;
}

export function isQuestionAnswered(
  answers: Record<string, { status: string; value: unknown }>,
  q: ConditionalQuestionDef,
): boolean {
  const a = answers[q.questionId];
  if (!a) return !q.required;
  if (a.status === 'stale') return false;
  if (a.status === 'skipped') return !q.required;
  if (a.status === 'unanswered') return !q.required;
  const v = a.value;
  if (typeof v === 'string') return v.trim().length > 0 || !q.required;
  if (typeof v === 'object' && v !== null && 'skipped' in v) {
    return (v as { skipped: boolean }).skipped === true || !q.required;
  }
  return a.status === 'answered' || a.status === 'confirmed' || a.status === 'suggested';
}

export function crowdLabel(type: CrowdType): string {
  return CROWD_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

export function rhythmLabel(type: RhythmType): string {
  return RHYTHM_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

export function isRhythmSkipped(rhythm: RhythmAnswer | undefined): boolean {
  return rhythm?.skipped === true;
}

export function patchConnectedCrowd(crowd: ConnectedCrowd, type: CrowdType): ConnectedCrowd {
  return type === 'other'
    ? { type, otherDescription: crowd.otherDescription ?? '' }
    : { type };
}

export function patchConnectedRhythm(
  rhythm: RhythmAnswer,
  type: RhythmType,
): RhythmAnswer {
  return {
    skipped: false,
    type,
    otherDescription: type === 'other' ? (rhythm.skipped ? '' : rhythm.otherDescription ?? '') : undefined,
  };
}
